import type {
  PatchAdminV1StoresStoreIdShippingRatesIdBody,
  PatchAdminV1StoresStoreIdShippingZonesIdBody,
  PostAdminV1StoresStoreIdShippingZonesBody,
  PostAdminV1StoresStoreIdShippingZonesIdRatesBody,
  ShippingRate,
  ShippingZone,
} from '@nucleus-commerce/api-client/admin';
import { zeroMoney } from '@nucleus-commerce/core';

import type { RateFormInput, RateFormOutput } from '../schemas/rate-form-schema';
import { parseWhole, rateFieldsFor } from '../schemas/rate-rules';
import type { ZoneFormInput, ZoneFormOutput } from '../schemas/zone-form-schema';

export function emptyZoneForm(position: number): ZoneFormInput {
  return { name: '', countries: [], regions: {}, position };
}

export function fromZone(zone: ShippingZone): ZoneFormInput {
  return {
    name: zone.name,
    countries: zone.countries,
    regions: zone.regions ?? {},
    position: zone.position,
  };
}

/**
 * Regions for countries no longer in the zone are dropped, and no restriction at all is sent as
 * null, which is how the backend spells "whole countries". Every field goes out on update too:
 * a PATCH that leaves `regions` out used to wipe them.
 */
function toZoneBody(
  values: ZoneFormOutput,
): PostAdminV1StoresStoreIdShippingZonesBody & PatchAdminV1StoresStoreIdShippingZonesIdBody {
  const entries = Object.entries(values.regions).filter(
    ([country, codes]) => values.countries.includes(country) && codes.length > 0,
  );
  return {
    name: values.name,
    countries: values.countries,
    regions: entries.length > 0 ? Object.fromEntries(entries) : null,
    position: values.position,
  };
}

export function toZoneCreate(values: ZoneFormOutput): PostAdminV1StoresStoreIdShippingZonesBody {
  return toZoneBody(values);
}

export function toZoneUpdate(values: ZoneFormOutput): PatchAdminV1StoresStoreIdShippingZonesIdBody {
  return toZoneBody(values);
}

const str = (value: number | null) => (value === null ? '' : String(value));

export function emptyRateForm(currency: string, position: number): RateFormInput {
  return {
    name: '',
    kind: 'flat',
    price: zeroMoney(currency),
    minWeightGrams: '',
    maxWeightGrams: '',
    minSubtotal: null,
    maxSubtotal: null,
    deliveryDaysMin: '',
    deliveryDaysMax: '',
    requiresSignature: false,
    isActive: true,
    position,
  };
}

export function fromRate(rate: ShippingRate): RateFormInput {
  return {
    name: rate.name,
    kind: rate.kind,
    price: rate.price,
    minWeightGrams: str(rate.minWeightGrams),
    maxWeightGrams: str(rate.maxWeightGrams),
    minSubtotal: rate.minSubtotal,
    maxSubtotal: rate.maxSubtotal,
    deliveryDaysMin: str(rate.deliveryDaysMin),
    deliveryDaysMax: str(rate.deliveryDaysMax),
    requiresSignature: rate.requiresSignature,
    isActive: rate.isActive,
    position: rate.position,
  };
}

/**
 * Only the inputs the kind uses are sent with a value; the rest go out as null so switching a
 * rate from weight to flat clears the stale band on the server. A free_over rate is priced at
 * zero, which is what the checkout charges for it anyway.
 */
export function toRateBody(
  values: RateFormOutput,
  currency: string,
): PostAdminV1StoresStoreIdShippingZonesIdRatesBody & PatchAdminV1StoresStoreIdShippingRatesIdBody {
  const fields = rateFieldsFor(values.kind);
  return {
    name: values.name,
    kind: values.kind,
    price: fields.price ? (values.price ?? zeroMoney(currency)) : zeroMoney(currency),
    minWeightGrams: fields.weightBand ? parseWhole(values.minWeightGrams) : null,
    maxWeightGrams: fields.weightBand ? parseWhole(values.maxWeightGrams) : null,
    minSubtotal: fields.subtotalBand || fields.threshold ? values.minSubtotal : null,
    maxSubtotal: fields.subtotalBand ? values.maxSubtotal : null,
    deliveryDaysMin: parseWhole(values.deliveryDaysMin),
    deliveryDaysMax: parseWhole(values.deliveryDaysMax),
    requiresSignature: values.requiresSignature,
    isActive: values.isActive,
    position: values.position,
  };
}
